import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useMemo, useRef, useState } from "react";
import { useReducedMotion } from "@/animation/gsap-motion";
import { introHeroStyles as styles } from "@/styles/classes";
import type { HeroSloganContent, Locale } from "@/types";

interface KineticSloganProps {
  slogan: HeroSloganContent;
  locale: Locale;
}

const ROTATION_INTERVAL_MS = 3200;
const TRANSITION_DURATION_SECONDS = 0.68;

export function KineticSlogan({ slogan, locale }: KineticSloganProps) {
  const rootRef = useRef<HTMLHeadingElement>(null);
  const viewportRef = useRef<HTMLSpanElement>(null);
  const trackRef = useRef<HTMLSpanElement>(null);
  const reducedMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const inlineTail = slogan.tail === "。";
  const reelPhrases = useMemo(
    () => slogan.phrases.length > 0 ? [...slogan.phrases, slogan.phrases[0]] : [],
    [slogan.phrases],
  );

  useGSAP(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || slogan.phrases.length === 0) return;

    let currentIndex = 0;
    let intervalId: number | undefined;
    let transition: gsap.core.Tween | undefined;
    let resizeObserver: ResizeObserver | undefined;
    let cancelled = false;

    const rowHeight = () => viewport.getBoundingClientRect().height;
    const moveToCurrentRow = () => {
      gsap.set(track, { y: -currentIndex * rowHeight() });
    };
    const stop = () => {
      if (intervalId !== undefined) window.clearInterval(intervalId);
      intervalId = undefined;
    };
    const advance = () => {
      const nextTrackIndex = currentIndex + 1;
      const nextActiveIndex = nextTrackIndex % slogan.phrases.length;
      const wrapsToStart = nextTrackIndex === slogan.phrases.length;

      transition?.kill();
      currentIndex = nextActiveIndex;
      setActiveIndex(nextActiveIndex);
      transition = gsap.to(track, {
        y: -nextTrackIndex * rowHeight(),
        duration: TRANSITION_DURATION_SECONDS,
        ease: "power4.inOut",
        overwrite: true,
        onComplete: () => {
          if (wrapsToStart) gsap.set(track, { y: 0 });
        },
      });
    };
    const start = () => {
      stop();
      if (!document.hidden) intervalId = window.setInterval(advance, ROTATION_INTERVAL_MS);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
        transition?.pause();
      } else {
        transition?.resume();
        start();
      }
    };

    gsap.set(track, { y: 0 });
    setActiveIndex(0);

    if (reducedMotion || slogan.phrases.length < 2) return;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(moveToCurrentRow);
      resizeObserver.observe(viewport);
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const startWhenFontsAreReady = () => {
      if (cancelled) return;
      moveToCurrentRow();
      start();
    };

    if (document.fonts?.status === "loading") {
      void document.fonts.ready.then(startWhenFontsAreReady);
    } else {
      startWhenFontsAreReady();
    }

    return () => {
      cancelled = true;
      stop();
      transition?.kill();
      resizeObserver?.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, { dependencies: [reducedMotion, slogan.phrases], scope: rootRef, revertOnUpdate: true });

  return (
    <h1 ref={rootRef} className={styles.kineticSlogan} data-locale={locale} aria-label={slogan.accessibleLabel}>
      <span className={styles.sloganVisual} aria-hidden="true">
        <span className={styles.sloganLead}>{slogan.lead}</span>
        <span ref={viewportRef} className={styles.sloganViewport}>
          <span ref={trackRef} className={styles.sloganTrack}>
            {reelPhrases.map((phrase, index) => {
              const isClone = index === slogan.phrases.length;
              return (
                <span
                  className={styles.sloganPhrase}
                  data-active={!isClone && index === activeIndex}
                  data-clone={isClone || undefined}
                  data-slogan-phrase
                  data-tracking={phrase.tracking}
                  key={`${phrase.text}-${index}`}
                >
                  {phrase.text}
                  {inlineTail && <span className={styles.sloganInlineTail}>{slogan.tail}</span>}
                </span>
              );
            })}
          </span>
        </span>
        {slogan.tail && !inlineTail && <span className={styles.sloganTail}>{slogan.tail}</span>}
      </span>
    </h1>
  );
}
